import { useEffect, useRef, useState, useCallback } from "react";
import jsQR from "jsqr";
import { X, Camera } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export function QRScannerDialog({ open, onOpenChange, onResult }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const [error, setError] = useState("");
  const [scanning, setScanning] = useState(false);

  const stop = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setScanning(false);
  }, []);

  const tick = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || video.readyState !== video.HAVE_ENOUGH_DATA) {
      rafRef.current = requestAnimationFrame(tick);
      return;
    }
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const code = jsQR(imageData.data, imageData.width, imageData.height);
    if (code?.data) {
      stop();
      onResult(code.data);
      return;
    }
    rafRef.current = requestAnimationFrame(tick);
  }, [onResult, stop]);

  const start = useCallback(async () => {
    setError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: { ideal: 1280 }, height: { ideal: 960 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setScanning(true);
      rafRef.current = requestAnimationFrame(tick);
    } catch {
      setError("Tidak dapat mengakses kamera. Sila benarkan akses kamera pada pelayar anda.");
    }
  }, [tick]);

  useEffect(() => {
    if (open) start();
    else stop();
    return () => stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="qr-scanner-dialog">
        <DialogHeader>
          <DialogTitle>Imbas Kod QR</DialogTitle>
          <DialogDescription>Halakan kamera ke kod QR pada kad ahli digital untuk pengesahan pantas.</DialogDescription>
        </DialogHeader>
        <div className="relative rounded-lg overflow-hidden bg-black" style={{ aspectRatio: "1/1" }}>
          <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
          {scanning && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-[70%] h-[70%] border-4 border-piks-gold/80 rounded-2xl shadow-[0_0_0_2000px_rgba(0,0,0,0.4)]" />
            </div>
          )}
          {!scanning && !error && (
            <div className="absolute inset-0 flex items-center justify-center text-white/70 text-sm">
              Memulakan kamera...
            </div>
          )}
        </div>
        {error && (
          <p className="text-sm text-piks-red" data-testid="qr-scanner-error">{error}</p>
        )}
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="qr-scanner-cancel-btn">
          <X className="w-4 h-4 mr-1.5" /> Batal
        </Button>
        <canvas ref={canvasRef} className="hidden" />
      </DialogContent>
    </Dialog>
  );
}

export function QRScanButton({ className, testId = "qr-scan-trigger-btn", label = "IMBAS QR", onResult }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button type="button" variant="outline" className={className} data-testid={testId} onClick={() => setOpen(true)}>
        <Camera className="w-4 h-4 mr-2" /> {label}
      </Button>
      <QRScannerDialog
        open={open}
        onOpenChange={setOpen}
        onResult={(text) => {
          setOpen(false);
          onResult(text);
        }}
      />
    </>
  );
}
